// src/design-system/Toast.tsx
import { CheckCircle2, Info, XCircle } from "lucide-react";
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";

import type { Tone } from "./tone";

type ToastItem = { id: number; tone: Tone; text: string };
type Ctx = { push: (text: string, tone?: Tone) => void };

const ToastContext = createContext<Ctx>({ push: () => undefined });
export const useToast = () => useContext(ToastContext);

const ICON = { ok: CheckCircle2, bad: XCircle, info: Info, warn: Info, gold: Info, mute: Info, violet: Info } as const;

/** تنبيه قصير بعد أي عملية، يختفي تلقائياً بلون يطابق النتيجة. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((text: string, tone: Tone = "ok") => {
    const id = Date.now() + Math.random();
    setItems((s) => [...s, { id, tone, text }]);
    setTimeout(() => setItems((s) => s.filter((t) => t.id !== id)), tone === "bad" ? 6000 : 3800);
  }, []);
  const value = useMemo(() => ({ push }), [push]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-5 start-5 z-[60] flex flex-col gap-2" aria-live="polite">
        {items.map((t) => {
          const Icon = ICON[t.tone];
          return (
            <div key={t.id} role="status" data-tone={t.tone}
              className="flex max-w-sm items-center gap-2.5 rounded-xl px-4 py-3 text-[13px]"
              style={{ background: "linear-gradient(145deg, rgba(9,24,39,.96), rgba(16,38,61,.96))", border: "1px solid var(--fg)", color: "var(--fg)", boxShadow: "0 0 26px color-mix(in srgb, var(--fg) 35%, transparent)" }}>
              <Icon size={16} aria-hidden /> <span className="text-ink">{t.text}</span>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
